import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { filter, switchMap, tap } from 'rxjs/operators';

import { Mapper, NgUtils, StringUtils, TiimePipesModule } from 'tiime-expert-utils';
import {
  SnackbarConfig,
  TiimeButtonModule,
  TiimeIconModule,
  TiimeInputContainerModule,
  TiimeMenuModule,
  TiimeOptionModule,
  TiimeOptionSelectionChange,
  TiimeSelectModule,
  TiimeSnackbarService
} from 'tiime-material';

import { DocumentApiService } from '@api-services/document-api.service';
import { SelectorBase } from '@bases/selector/selector.base';
import { DocumentCategoryOwner } from '@enums/document-category-owner.enum';
import { DocumentCategory } from '@models/document-category';
import { DocumentCategoryIdentifierIconModule } from '@modules/shared/components/document-category-identifier-icon/document-category-identifier-icon.module';
import { ConfirmDialogData } from '@modules/shared/dialogs/confirm-dialog/confirm-dialog-data';
import { ConfirmDialogModule } from '@modules/shared/dialogs/confirm-dialog/confirm-dialog.module';
import { ConfirmDialogService } from '@modules/shared/dialogs/confirm-dialog/confirm-dialog.service';
import { CreateDocumentCategoryDialogData } from '@modules/shared/dialogs/create-document-category-dialog/create-document-category-dialog-data';
import { CreateDocumentCategoryDialogModule } from '@modules/shared/dialogs/create-document-category-dialog/create-document-category-dialog.module';
import { CreateDocumentCategoryDialogService } from '@modules/shared/dialogs/create-document-category-dialog/create-document-category-dialog.service';
import { FormFocusObserveDirective } from '@modules/shared/directives/form-focus-observe/form-focus-observe.directive';

@Component({
  standalone: true,
  selector: 'app-document-category-select',
  templateUrl: './document-category-select.component.html',
  styleUrls: ['./document-category-select.component.scss'],
  imports: [
    TiimePipesModule,
    DocumentCategoryIdentifierIconModule,
    CreateDocumentCategoryDialogModule,
    ConfirmDialogModule,
    FormFocusObserveDirective,
    TiimeInputContainerModule,
    TiimeSelectModule,
    ReactiveFormsModule,
    CommonModule,
    TiimeButtonModule,
    TiimeMenuModule,
    TiimeOptionModule,
    TiimeIconModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentCategorySelectComponent extends SelectorBase<DocumentCategory> {
  @Input() documentCategoryControl: FormControl;
  @Input('documentCategories') set setDocumentCategories(documentCategories: DocumentCategory[]) {
    if (documentCategories) {
      this.selectorData = documentCategories;
      this.filteredSelectorData = documentCategories;
    }
  }
  @Input() errorMessage: string;
  @Input() displayMenu = true;
  @Input() disableCreate = false;

  @Output() readonly createdDocumentCategory: EventEmitter<DocumentCategory> = new EventEmitter<DocumentCategory>();
  @Output() readonly deletedDocumentCategory: EventEmitter<number> = new EventEmitter<number>();

  readonly trackById = NgUtils.trackById;
  readonly DocumentCategoryOwner = DocumentCategoryOwner;

  readonly mapToSelectedDocumentCategory: Mapper<number, DocumentCategory> = (
    documentCategoryId: number,
    documentCategories: DocumentCategory[]
  ) => documentCategories?.find((documentCategory: DocumentCategory) => documentCategory.id === documentCategoryId);
  readonly mapToDocumentCategories: Mapper<DocumentCategory[], DocumentCategory[]> = (
    documentCategories: DocumentCategory[],
    documentCategoryOwner: DocumentCategoryOwner
  ) => documentCategories?.filter(documentCategory => documentCategory.owner === documentCategoryOwner);

  constructor(
    private createDocumentCategoryDialogService: CreateDocumentCategoryDialogService,
    private confirmDialogService: ConfirmDialogService,
    private documentApiService: DocumentApiService,
    private snackbar: TiimeSnackbarService
  ) {
    super();
  }

  deleteDocumentCategory(): void {
    this.confirmDialogService
      .open({
        data: new ConfirmDialogData(
          'Attention',
          `Êtes-vous sûr de vouloir supprimer cet espace ?`,
          'Supprimer',
          'Annuler',
          null,
          'warn'
        )
      })
      .afterClosed()
      .pipe(
        filter(confirm => !!confirm),
        switchMap(() => this.documentApiService.deleteDocumentCategory(this.documentCategoryControl.value)),
        tap(() => this.snackbar.open(`L'espace a bien été supprimé`, SnackbarConfig.success)),
        tap(() => this.deletedDocumentCategory.emit(this.documentCategoryControl.value))
      )
      .subscribe();
  }

  openCreateDocumentCategoryDialog(event: TiimeOptionSelectionChange): void {
    if (!event.isUserInput) {
      return;
    }

    const data: CreateDocumentCategoryDialogData = {
      name: this.searchControl.value
    };

    this.createDocumentCategoryDialogService
      .open({ data })
      .afterClosed()
      .pipe(
        filter((documentCategory: DocumentCategory) => !!documentCategory),
        tap((documentCategory: DocumentCategory) => this.createdDocumentCategory.emit(documentCategory))
      )
      .subscribe();
  }

  filterFunction(search: string): DocumentCategory[] {
    return this.selectorData.filter((documentCategory: DocumentCategory) =>
      StringUtils.normalizeToLowerCase(documentCategory.name).includes(StringUtils.normalizeToLowerCase(search))
    );
  }
}
